import React from 'react';

import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { DialogActions, DialogContent, Divider, InputLabel, Typography } from '@mui/material';
import { AlineInput } from 'src/AlineInput';
import { AccountsAutocomplete, AlineTextarea, PermissionRoleMenu, WhatsThis } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack, useDocumentParams } from 'src/helpers';
import { useGetOrgMembers, useIsAuthorizedToEditStorable } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { AlineButton, AlineDialog, AlineTooltip, Spacer } from '..';

type EmailList = string[];

interface ShareDocumentViaEmailDialogState {
  message: string;
  nameVersion: string;
  open: boolean;
  subject: string;
  // Array of email addresses.
  usersToCopyTo: { role: Apollo.PermissionRole; to: EmailList };
  usersToSendTo: { role: Apollo.PermissionRole; to: EmailList };
}

export const initialShareDocumentViaEmailDialogState: ShareDocumentViaEmailDialogState = {
  message: '',
  nameVersion: '',
  open: false,
  subject: '',
  usersToCopyTo: { role: Apollo.PermissionRole.Viewer, to: [] },
  usersToSendTo: { role: Apollo.PermissionRole.Viewer, to: [] },
};

const shareDocumentViaEmailStateVar = makeVar<ShareDocumentViaEmailDialogState>(
  initialShareDocumentViaEmailDialogState
);

export function useShareDocumentViaEmailDialogState(): ShareDocumentViaEmailDialogState {
  return useReactiveVar(shareDocumentViaEmailStateVar);
}

export function setShareDocumentViaEmailDialogState(
  stateOrCallback:
    | Partial<ShareDocumentViaEmailDialogState>
    | ((state: ShareDocumentViaEmailDialogState) => ShareDocumentViaEmailDialogState)
): void {
  const existingState = shareDocumentViaEmailStateVar();
  const newState =
    typeof stateOrCallback === 'function' ? stateOrCallback(existingState) : stateOrCallback;
  shareDocumentViaEmailStateVar({ ...existingState, ...newState });
}

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  container: {
    width: theme.dialogStyles.mediumDialogWidth,
  },
  // Box sizing border-box and a bit of padding is needed to prevent a weird horizontal scroll on the dialog content.
  content: {
    boxSizing: 'border-box',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
    width: '100%',
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  nameVersion: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sendButton: {
    marginLeft: theme.spacing(1),
  },
  subRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
  },
  text: {
    ...theme.appTypography.fieldLabel,
    fontSize: 14,
  },
}));

export function ShareDocumentViaEmailDialog() {
  const { classes } = useStyles();
  const { documentId } = useDocumentParams();
  const { open } = useShareDocumentViaEmailDialogState();

  function handleClose(): void {
    setShareDocumentViaEmailDialogState({ open: false });
  }

  return (
    <AlineDialog
      TransitionProps={{
        onExit: () => {
          setTimeout(
            () => setShareDocumentViaEmailDialogState(initialShareDocumentViaEmailDialogState),
            250
          );
        },
      }}
      classes={{ paperWidthMd: classes.container }}
      open={open}
      title="Share via email"
      onClose={handleClose}
    >
      <DialogContent className={classes.content}>
        <ShareDocumentViaEmailBody />
      </DialogContent>
      <Divider className={classes.divider} variant="fullWidth" />
      <ShareDocumentViaEmailActions documentId={documentId} />
    </AlineDialog>
  );
}

function ShareDocumentViaEmailBody() {
  const { classes } = useStyles();
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });
  const { message, nameVersion, subject, usersToCopyTo, usersToSendTo } =
    useShareDocumentViaEmailDialogState();

  return (
    <>
      <div className={classes.row}>
        <InputLabel>
          <Typography className={classes.text}>To</Typography>
        </InputLabel>
        <div className={classes.subRow}>
          <AccountsAutocomplete
            accounts={orgMembers}
            placeholder="Search users or add email..."
            value={usersToSendTo.to.map(email => ({ __typename: 'NewUser', email }))}
            onChange={(_event, value) => {
              const emailList: string[] = value.flatMap(val => {
                return val.__typename === 'Org' ? [] : [val.email.toLowerCase().trim()];
              });
              setShareDocumentViaEmailDialogState({
                usersToSendTo: {
                  role: usersToSendTo.role,
                  to: emailList,
                },
              });
            }}
          />
          <PermissionRoleMenu
            handleSelect={role => {
              setShareDocumentViaEmailDialogState({
                usersToSendTo: {
                  role: role,
                  to: usersToSendTo.to,
                },
              });
            }}
            value={usersToSendTo.role}
          />
        </div>
      </div>
      <div className={classes.row}>
        <InputLabel>
          <Typography className={classes.text}>Cc</Typography>
        </InputLabel>
        <div className={classes.subRow}>
          <AccountsAutocomplete
            accounts={orgMembers}
            placeholder="Search users or add email..."
            value={usersToCopyTo.to.map(email => ({ __typename: 'NewUser', email }))}
            onChange={(_event, value) => {
              const emailList: string[] = value.flatMap(val => {
                return val.__typename === 'Org' ? [] : [val.email.toLowerCase().trim()];
              });
              setShareDocumentViaEmailDialogState({
                usersToCopyTo: {
                  role: usersToCopyTo.role,
                  to: emailList,
                },
              });
            }}
          />
          <PermissionRoleMenu
            handleSelect={role => {
              setShareDocumentViaEmailDialogState({
                usersToCopyTo: {
                  role: role,
                  to: usersToCopyTo.to,
                },
              });
            }}
            value={usersToCopyTo.role}
          />
        </div>
      </div>
      <div className={classes.row}>
        <InputLabel>
          <Typography className={classes.text}>Subject</Typography>
        </InputLabel>
        <AlineInput
          placeholder="Type email subject line..."
          value={subject}
          onChange={e => {
            setShareDocumentViaEmailDialogState({
              subject: e.target.value,
            });
          }}
        />
      </div>
      <div className={classes.row}>
        <div className={classes.nameVersion}>
          <InputLabel>
            <Typography className={classes.text}>Name Version</Typography>
          </InputLabel>
          <Spacer />
          <WhatsThis tooltipMsg="Adding a version name saves the current draft as a new version when sending via email." />
        </div>
        <AlineInput
          placeholder="Type name to save draft as a new version..."
          value={nameVersion}
          onChange={e => {
            setShareDocumentViaEmailDialogState({
              nameVersion: e.target.value,
            });
          }}
        />
      </div>
      <div className={classes.row}>
        <InputLabel>
          <Typography className={classes.text}>Message</Typography>
        </InputLabel>
        <AlineTextarea
          minRows={5}
          placeholder="Add a message for recipients..."
          value={message}
          onChange={e => {
            setShareDocumentViaEmailDialogState({
              message: e.target.value,
            });
          }}
        />
      </div>
    </>
  );
}

function ShareDocumentViaEmailActions({ documentId }: { documentId: UUID }) {
  const { classes } = useStyles();
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const [sendDocViaEmail, { loading: sendingDocViaEmail }] =
    Apollo.useSendDocumentViaEmailMutation();
  const [sendDocViaEmailTest, { loading: sendingDocViaEmailTest }] =
    Apollo.useSendDocumentViaEmailTestMutation();
  const { message, nameVersion, subject, usersToCopyTo, usersToSendTo } =
    useShareDocumentViaEmailDialogState();
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Email sent to the listed recipients!',
  });
  const dispatchTestSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Test email sent to your inbox!',
  });
  const disabledReason = !canEdit
    ? 'Only editors can send via email'
    : !usersToSendTo.to.length
      ? 'Add recipients for your email'
      : !subject
        ? 'Add a subject'
        : '';

  function handleClose(): void {
    setShareDocumentViaEmailDialogState(initialShareDocumentViaEmailDialogState);
  }

  return (
    <DialogActions className={classes.actions}>
      <AlineButton
        color="primary"
        disabled={sendingDocViaEmail}
        loading={sendingDocViaEmailTest}
        variant="outlined"
        onClick={async () => {
          const { data } = await sendDocViaEmailTest({
            variables: {
              input: {
                documentId,
                message,
                subject,
              },
            },
          });
          if (data) dispatchTestSuccessSnack();
        }}
      >
        Preview Email
      </AlineButton>
      <Spacer />
      <AlineButton
        color="primary"
        disabled={sendingDocViaEmail || sendingDocViaEmailTest}
        variant="outlined"
        onClick={handleClose}
      >
        Cancel
      </AlineButton>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div className={classes.sendButton}>
          <AlineButton
            color="primary"
            disabled={Boolean(disabledReason) || sendingDocViaEmailTest}
            loading={sendingDocViaEmail}
            variant="contained"
            onClick={async () => {
              const { data } = await sendDocViaEmail({
                variables: {
                  input: {
                    copyTo: {
                      role: usersToCopyTo.role,
                      to: usersToCopyTo.to,
                    },
                    documentId,
                    message,
                    nameVersion,
                    sendTo: { role: usersToSendTo.role, to: usersToSendTo.to },
                    subject,
                  },
                },
              });
              handleClose();
              if (data) dispatchSuccessSnack();
            }}
          >
            Send
          </AlineButton>
        </div>
      </AlineTooltip>
    </DialogActions>
  );
}
